import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, Popconfirm, Select, Table, Tag } from 'antd';
import heIL from 'antd/locale/he_IL';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCashRegister, faPrint, faBan } from '@fortawesome/free-solid-svg-icons';
import { Money } from '../components/Money';
import { ReceiptAmount } from '../components/ReceiptAmount';
import { Loader } from '../components/Loader';
import { EmptyState } from '../components/EmptyState';
import ReportingPeriodFilter from '../components/ReportingPeriodFilter';
import { useRegisterRefresh } from '../components/PageActions';
import { CloseRegisterDrawer } from '../features/cash/CloseRegisterDrawer';
import { PdfDrawer } from '../features/cash/PdfDrawer';
import {
  useCancelClosing,
  useCashRegisters,
  useClosings,
  useRegisterCurrent,
} from '../hooks/useCashFlow';
import { useLocationContext } from '../auth/LocationContext';
import { useProjectContext } from '../auth/ProjectContext';
import { firstOfMonthIsrael, lastOfMonthIsrael, isoToDisplay } from '../utils/dates';
import type { ClosingSummary, Receipt } from '@deposits/shared';

type View = 'register' | 'closings';

const STATUS_TAG: Record<ClosingSummary['status'], { label: string; color: string }> = {
  in_safe: { label: 'בכספת', color: 'blue' },
  closed: { label: 'נסגר ללא הפקדה', color: 'default' },
  deposited: { label: 'הופקד', color: 'green' },
  cancelled: { label: 'מבוטל', color: 'red' },
};

export function RegisterCloseTab({ view }: { view: View }) {
  const { selectedLocationId } = useLocationContext();
  const { selectedProjectId } = useProjectContext();
  const [period, setPeriod] = useState(() => ({ from: firstOfMonthIsrael(), to: lastOfMonthIsrael() }));
  const [registerId, setRegisterId] = useState<number | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [pdfClosingId, setPdfClosingId] = useState<number | null>(null);

  const { data: registers = [], isLoading: registersLoading } = useCashRegisters(selectedLocationId, selectedProjectId);
  // A register picked in another project/location isn't in this list — fall
  // back to the first one rather than keep showing the stale register.
  const effectiveRegisterId =
    (registerId != null && registers.some((r) => r.id === registerId) ? registerId : null) ??
    registers[0]?.id ??
    null;

  const { data: current, isFetching: currentFetching, isError: currentError, refetch: refetchCurrent } =
    useRegisterCurrent(view === 'register' ? effectiveRegisterId : null);

  // Receipt selection — default to all whenever the underlying set changes
  // (register switch / refetch). The user can deselect to close a subset.
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const receipts = current?.receipts ?? [];
  const receiptIdsKey = receipts.map((r) => r.external_receipt_id).join(',');
  useEffect(() => {
    setSelectedKeys(receiptIdsKey ? receiptIdsKey.split(',') : []);
  }, [receiptIdsKey]);

  const selectedReceipts = useMemo(
    () => receipts.filter((r) => selectedKeys.includes(r.external_receipt_id)),
    [receipts, selectedKeys],
  );
  const selectedTotal = useMemo(
    () => selectedReceipts.reduce((s, r) => s + r.amount, 0),
    [selectedReceipts],
  );

  const listParams = useMemo(() => {
    if (selectedLocationId == null) return null;
    return {
      location_id: selectedLocationId,
      project_id: selectedProjectId ?? undefined,
      from: period.from,
      to: period.to,
      status: 'all' as const,
    };
  }, [selectedLocationId, selectedProjectId, period]);
  const { data: closings = [], isFetching: closingsFetching, isError: closingsError, refetch: refetchClosings } =
    useClosings(view === 'closings' ? listParams : null);

  useRegisterRefresh(useCallback(() => {
    refetchCurrent();
    refetchClosings();
  }, [refetchCurrent, refetchClosings]));

  const cancelMutation = useCancelClosing();

  if (selectedLocationId == null) return <Loader fullScreen />;

  const noRegisters = !registersLoading && registers.length === 0;

  const receiptColumns = [
    { title: 'תאריך', dataIndex: 'issue_date', width: 110, render: (v: string) => isoToDisplay(v) },
    {
      title: 'מספר קבלה',
      dataIndex: 'receipt_number',
      width: 120,
      render: (_: unknown, r: Receipt) =>
        r.receipt_url ? (
          <a href={r.receipt_url} target="_blank" rel="noopener noreferrer" dir="ltr" className="text-primary hover:underline">
            {r.receipt_number}
          </a>
        ) : (
          <span dir="ltr">{r.receipt_number}</span>
        ),
    },
    { title: 'שם לקוח', dataIndex: 'customer_name', ellipsis: true },
    { title: 'סכום', dataIndex: 'amount', width: 110, render: (_: number, r: Receipt) => <ReceiptAmount receipt={r} /> },
  ];

  const closingColumns = [
    { title: 'מספר', dataIndex: 'closing_number', width: 80, render: (v: number) => <span dir="ltr">{v}</span> },
    { title: 'תאריך', dataIndex: 'closing_date', width: 110, render: (v: string) => isoToDisplay(v) },
    { title: 'קופה', dataIndex: 'register_name', ellipsis: true, render: (v: string | null) => v ?? '—' },
    { title: 'צפי', dataIndex: 'expected_cash', width: 110, render: (v: number) => <Money amount={v} /> },
    { title: 'נספר', dataIndex: 'counted_amount', width: 110, render: (v: number) => <Money amount={v} /> },
    {
      title: 'הפרש',
      dataIndex: 'variance',
      width: 100,
      render: (v: number) => (
        <span className={v === 0 ? '' : v > 0 ? 'text-success-green' : 'text-error-red'}>
          <Money amount={v} />
        </span>
      ),
    },
    { title: 'לכספת', dataIndex: 'amount_to_safe', width: 110, render: (v: number) => <Money amount={v} /> },
    {
      title: 'סטטוס',
      dataIndex: 'status',
      width: 90,
      render: (v: ClosingSummary['status']) => <Tag color={STATUS_TAG[v].color}>{STATUS_TAG[v].label}</Tag>,
    },
    {
      title: 'פעולות',
      width: 160,
      render: (_: unknown, r: ClosingSummary) => (
        <div className="flex items-center gap-1">
          <Button size="small" icon={<FontAwesomeIcon icon={faPrint} />} onClick={() => setPdfClosingId(r.id)}>
            הדפסה
          </Button>
          {(r.status === 'in_safe' || r.status === 'closed') && (
            <Popconfirm
              title="לבטל את סגירת הקופה?"
              description="התקבולים יחזרו לקופה."
              okText="בטל סגירה"
              cancelText="חזרה"
              onConfirm={() =>
                cancelMutation.mutate(
                  { id: r.id },
                  {
                    onSuccess: () => toast.success('הסגירה בוטלה'),
                    onError: (err) => toast.error(err.message || 'שגיאה בביטול'),
                  },
                )
              }
            >
              <Button size="small" danger icon={<FontAwesomeIcon icon={faBan} />}>
                בטל
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center gap-3 flex-wrap pb-3">
        {view === 'register' && (
          <Select
            value={effectiveRegisterId ?? undefined}
            onChange={(v) => setRegisterId(v)}
            placeholder="בחר קופה"
            loading={registersLoading}
            options={registers.map((r) => ({ value: r.id, label: r.name }))}
            style={{ minWidth: 200 }}
            notFoundContent="לא הוגדרו קופות"
          />
        )}
        <div className="flex-1" />
        {view === 'closings' && (
          <ReportingPeriodFilter value={period} onChange={(from, to) => setPeriod({ from, to })} />
        )}
      </div>

      {noRegisters ? (
        <EmptyState
          title="לא הוגדרו קופות בסניף זה"
          description="הוסף קופה במסך ההגדרות (⚙️) כדי להתחיל"
        />
      ) : view === 'register' ? (
        <>
          {/* Live register balance — the raw accumulating cash. */}
          <div className="bg-blue-bg border border-border rounded p-4 mb-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faCashRegister} className="text-primary" style={{ fontSize: 18 }} />
              <div>
                <div className="text-secondary-text text-small">
                  יתרת הקופה {current ? `· ${current.name}` : ''}
                </div>
                <div className="text-heading-2 font-semibold">
                  <Money amount={current?.current_balance ?? 0} />
                </div>
                {current && (
                  <div className="text-small text-secondary-text mt-0.5">
                    עודף פתיחה <Money amount={current.opening_balance} /> + מכירות מזומן{' '}
                    <Money amount={current.cash_sales_total} /> ({current.receipt_count} תקבולים)
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button
                type="primary"
                icon={<FontAwesomeIcon icon={faCashRegister} />}
                disabled={effectiveRegisterId == null || current == null}
                onClick={() => setCloseOpen(true)}
              >
                {selectedReceipts.length > 0 ? `סגור קופה (${selectedReceipts.length})` : 'סגור קופה על 0'}
              </Button>
              {selectedReceipts.length > 0 && (
                <div className="text-small text-secondary-text">
                  נבחר: <Money amount={selectedTotal} />
                </div>
              )}
            </div>
          </div>

          {/* The raw cash receipts that make up the balance. */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
              <Table<Receipt>
                rowKey="external_receipt_id"
                columns={receiptColumns as any}
                dataSource={receipts}
                loading={currentFetching ? { spinning: true } : false}
                size="small"
                rowSelection={{
                  selectedRowKeys: selectedKeys,
                  onChange: (keys) => setSelectedKeys(keys.map(String)),
                }}
                pagination={{ defaultPageSize: 50, showSizeChanger: true, size: 'small' }}
                scroll={{ x: 'max-content', y: 'calc(100vh - 420px)' }}
                locale={{
                  emptyText: currentError ? (
                    <EmptyState title="שגיאה בטעינת הקופה" description="נסה שוב או רענן את העמוד" />
                  ) : (
                    <EmptyState title="אין מזומן בקופה" description="לא נמצאו תקבולי מזומן פתוחים לקופה זו" />
                  ),
                }}
              />
            </ConfigProvider>
          </div>
        </>
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden">
          <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
            <Table<ClosingSummary>
              rowKey="id"
              columns={closingColumns as any}
              dataSource={closings}
              loading={closingsFetching ? { spinning: true } : false}
              size="small"
              pagination={{ defaultPageSize: 50, showSizeChanger: true, size: 'small' }}
              scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
              locale={{
                emptyText: closingsError ? (
                  <EmptyState title="שגיאה בטעינת סגירות הקופה" description="נסה שוב או רענן את העמוד" />
                ) : (
                  <EmptyState title="אין סגירות קופה בטווח" description="סגור קופה כדי להתחיל" />
                ),
              }}
            />
          </ConfigProvider>
        </div>
      )}

      <CloseRegisterDrawer
        open={closeOpen}
        registerId={effectiveRegisterId}
        registerName={current?.name ?? ''}
        openingBalance={current?.opening_balance ?? 0}
        selectedReceipts={selectedReceipts}
        onClose={() => setCloseOpen(false)}
        onCreated={(id) => {
          setCloseOpen(false);
          setPdfClosingId(id);
        }}
      />

      <PdfDrawer
        open={pdfClosingId !== null}
        title="סגירת קופה"
        blobPath={pdfClosingId !== null ? `/register-closings/${pdfClosingId}/pdf-blob` : null}
        downloadName={`closing-${pdfClosingId ?? ''}.pdf`}
        onClose={() => setPdfClosingId(null)}
      />
    </div>
  );
}
